import { alpha, Box, Divider, Link, Skeleton, Stack, styled, Typography } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import {
  AccordionViewList,
  ActionItem,
  ActionItemListItemSkeleton,
  ActionItemsTable,
  SnackbarContent,
  useDrawer,
} from "../../../components";
import {
  ACTION_ITEM_STATUSES,
  ClosedActionItemStatuses,
  Links,
  SortByDirection,
  UserStrategyFilterByOption,
  UserStrategyGroupByOption,
  UserStrategySortByOption,
} from "../../../constants";
import { useSession } from "../../../hooks";
import { ActionItemSummaryDto, useGetUserStrategicOwnership, UserDto } from "../../../http";
import { useUserStrategySettings } from "./hooks";

const AccordionContainer = styled(Box)`
  border: dashed;
  border-color: ${({ theme }) => alpha(theme.palette.common.black, 0.3)};
  border-width: 1px;
  border-radius: 8px;
`;

interface IProps {
  userGuid: string;
  isTableView: boolean;
  onLoaded?: () => void;
}

const UserStrategicOwnershipContent = (props: IProps) => {
  const { userId: sessionUserGuid } = useSession();
  const { data, isLoading } = useGetUserStrategicOwnership(props.userGuid);
  const { settings } = useUserStrategySettings(props.userGuid);
  const [groupedActionItems, setGroupedActionItems] = useState<_.Dictionary<ActionItemSummaryDto[]> | undefined>(
    undefined
  );
  const { closeDrawer } = useDrawer();

  useEffect(() => {
    if (!isLoading) {
      props.onLoaded?.();
    }
  }, [isLoading, props]);

  const groupByFunction = useCallback(
    (item: ActionItemSummaryDto) => {
      switch (settings.groupByOption) {
        case UserStrategyGroupByOption.None:
          return "All Items";
        case UserStrategyGroupByOption.Team:
          return item.teamPlanSection.teamName;
        case UserStrategyGroupByOption.Status:
          return ACTION_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.name;
        case UserStrategyGroupByOption.DueDate: {
          if (item.dueDate == null) {
            return "No Due Date";
          }

          const dueDate = DateTime.fromISO(item.dueDate);

          if (dueDate < DateTime.now()) {
            return "Past Due";
          } else if (dueDate < DateTime.now().plus({ week: 1 })) {
            return "Due Soon";
          } else {
            return "Due Later";
          }
        }
      }
    },
    [settings.groupByOption]
  );

  const groupSortByFunction = useCallback(
    (item: ActionItemSummaryDto) => {
      switch (settings.groupByOption) {
        case UserStrategyGroupByOption.Team:
          return item.teamPlanSection.teamName;
        case UserStrategyGroupByOption.Status:
          return ACTION_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.sortOrder;
        case UserStrategyGroupByOption.DueDate:
          return item.dueDate;
        case UserStrategyGroupByOption.None:
        default:
          return;
      }
    },
    [settings.groupByOption]
  );

  const filterByFunction = useCallback(
    (item: ActionItemSummaryDto) => {
      switch (settings.filterByOption) {
        case UserStrategyFilterByOption.Status: {
          if (settings.filterByStatusOption) {
            return item.currentStatus === settings.filterByStatusOption;
          } else {
            return false;
          }
        }
        case UserStrategyFilterByOption.All:
        default:
          return true;
      }
    },
    [settings.filterByOption, settings.filterByStatusOption]
  );

  function sortByFunction(a: ActionItemSummaryDto, b: ActionItemSummaryDto) {
    switch (settings.sortByOption) {
      case UserStrategySortByOption.DateModified:
        if (settings.sortByDirection === SortByDirection.Ascending) {
          return (
            DateTime.fromISO(a.lastModifiedDateTimeUtc, { zone: "utc" }).toLocal().toMillis() -
            DateTime.fromISO(b.lastModifiedDateTimeUtc, { zone: "utc" }).toLocal().toMillis()
          );
        } else {
          return (
            DateTime.fromISO(b.lastModifiedDateTimeUtc, { zone: "utc" }).toLocal().toMillis() -
            DateTime.fromISO(a.lastModifiedDateTimeUtc, { zone: "utc" }).toLocal().toMillis()
          );
        }

      case UserStrategySortByOption.DateCreated:
        if (settings.sortByDirection === SortByDirection.Ascending) {
          return (
            DateTime.fromISO(a.createdDateTimeUtc, { zone: "utc" }).toLocal().toMillis() -
            DateTime.fromISO(b.createdDateTimeUtc, { zone: "utc" }).toLocal().toMillis()
          );
        } else {
          return (
            DateTime.fromISO(b.createdDateTimeUtc, { zone: "utc" }).toLocal().toMillis() -
            DateTime.fromISO(a.createdDateTimeUtc, { zone: "utc" }).toLocal().toMillis()
          );
        }

      default:
        return 1;
    }
  }

  useEffect(() => {
    if (data !== undefined) {
      const actionItems = _.groupBy(
        _.sortBy(data.actionItems.filter(filterByFunction), groupSortByFunction),
        groupByFunction
      );
      setGroupedActionItems(actionItems);
    }
  }, [data, filterByFunction, groupByFunction, groupSortByFunction]);

  function handleOwnershipChanged(actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) {
    if (removedOwners.some((x) => x.userId === sessionUserGuid)) {
      enqueueSnackbar(
        <SnackbarContent
          title="Success"
          content={
            <>
              <span>Your ownership was removed from </span>
              <Link href={Links.ActionItemDetail(actionItemGuid)}>{actionItemDescription}</Link>
            </>
          }
        />,
        {
          variant: "success",
        }
      );

      // Close drawer incase it's open.
      closeDrawer();
    }
  }

  return (
    <Stack spacing={1}>
      {isLoading || groupedActionItems == null ? (
        <AccordionContainer>
          <Stack spacing={0.5} divider={<Divider variant="dashed" />} sx={{ py: 0.5, px: 1 }}>
            {[...Array(3)].map((_, i) =>
              props.isTableView ? (
                <Skeleton variant="rectangular" key={i} height={30} />
              ) : (
                <ActionItemListItemSkeleton key={i} />
              )
            )}
          </Stack>
        </AccordionContainer>
      ) : _.keys(groupedActionItems).length === 0 ? (
        <Typography>Nothing here yet.</Typography>
      ) : (
        _.keys(groupedActionItems).map((groupName, index) => (
          <AccordionContainer key={index}>
            <AccordionViewList
              title={groupName}
              dataSource={groupedActionItems[groupName]}
              isLoading={isLoading}
              divider={<Divider variant="dashed" sx={{ mx: 1 }} />}
              renderContent={
                props.isTableView
                  ? (items, _closed) => (
                      <ActionItemsTable
                        tableId={`userStrategicOwnership-${props.userGuid}`}
                        actionItems={items.sort(sortByFunction)}
                        enableActions={sessionUserGuid === props.userGuid}
                        onOwnershipChanged={handleOwnershipChanged}
                        fetchActionItemSubitems={() => Promise.resolve()}
                      />
                    )
                  : undefined
              }
              renderItem={(item) => (
                <ActionItem
                  actionItemGuid={item.guid}
                  canEdit={item.userCanEdit}
                  canEditPlan={item.userCanEdit && sessionUserGuid === props.userGuid}
                  showTeamChip
                  onOwnershipChanged={handleOwnershipChanged}
                  sx={{ px: 0.5 }}
                />
              )}
              activeItemsListProps={{
                activeItemsSortFunction: sortByFunction,
              }}
              closedItemsListProps={
                settings.groupByOption !== UserStrategyGroupByOption.Status
                  ? {
                      closedItemsFilter: (item) => ClosedActionItemStatuses.includes(item.currentStatus),
                      closedItemsSortFunction: sortByFunction,
                    }
                  : undefined
              }
            />
          </AccordionContainer>
        ))
      )}
    </Stack>
  );
};

export { UserStrategicOwnershipContent };
